import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Skeleton,
} from '@mui/material'
import { ContactInteraction, contactInteractionsApi } from '../../services/api'
import {
  DEFAULT_INTERACTION_TYPE,
  INTERACTION_TYPE_OPTIONS,
  interactionTypeLabel,
  interactionDate,
  sortInteractionsByDate,
} from '../../constants/networking'

interface InterviewFollowUpTimelineProps {
  interviewId: string
}

// The vocabulary of "what happened" is one list, shared with the networking
// flow — these two write the same contactInteractions record through the same
// lambda. It used to be copied here; now both read the same constant so they
// cannot drift.
const interactionTypes = INTERACTION_TYPE_OPTIONS

// Follow-up history for the hiring-side conversation on this interview (the
// interviewer, tracked per-round as interviewerName/interviewerTitle) — the
// interview-half counterpart to ContactDetailPanel's networking timeline.
// Reuses contactInteractionsApi and the same activity-timeline rendering
// pattern, keyed by interviewId instead of contactId.
export default function InterviewFollowUpTimeline({ interviewId }: InterviewFollowUpTimelineProps) {
  const [interactions, setInteractions] = useState<ContactInteraction[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [newType, setNewType] = useState<string>(DEFAULT_INTERACTION_TYPE)
  const [newNotes, setNewNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    contactInteractionsApi
      .list({ interviewId })
      .then((result) => {
        if (!cancelled) setInteractions(result)
      })
      .catch((err) => {
        console.error('Failed to load follow-up history:', err)
        if (!cancelled) setLoadError('Failed to load follow-up history')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [interviewId])

  const handleLog = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const interaction = await contactInteractionsApi.create({
        interviewId,
        type: newType,
        notes: newNotes,
      })
      setInteractions((prev) => [interaction, ...prev])
      setNewNotes('')
      setNewType(DEFAULT_INTERACTION_TYPE)
    } catch (err) {
      console.error('Failed to log follow-up:', err)
      setSaveError('Failed to log follow-up')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 1 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="follow-up-type-label">Type</InputLabel>
          <Select
            labelId="follow-up-type-label"
            label="Type"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
          >
            {interactionTypes.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="contained" size="small" onClick={handleLog} disabled={saving}>
          {saving ? 'Logging…' : 'Log'}
        </Button>
      </Box>
      <TextField
        size="small"
        fullWidth
        multiline
        rows={2}
        label="Notes (optional)"
        value={newNotes}
        onChange={(e) => setNewNotes(e.target.value)}
        sx={{ mb: 1 }}
      />
      {saveError && (
        <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
          {saveError}
        </Typography>
      )}

      <Box sx={{ mt: 1 }}>
        {loading ? (
          <>
            <Skeleton variant="rounded" width="100%" height={32} sx={{ mb: 1 }} />
            <Skeleton variant="rounded" width="100%" height={32} sx={{ mb: 1 }} />
          </>
        ) : loadError ? (
          <Typography variant="body2" color="error.main">
            {loadError}
          </Typography>
        ) : interactions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No follow-up logged yet
          </Typography>
        ) : (
          <List dense disablePadding>
            {sortInteractionsByDate(interactions).map((i) => (
              <ListItem key={i.id} disablePadding sx={{ mb: 1 }}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        label={interactionTypeLabel(i.type)}
                        size="small"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {new Date(interactionDate(i)).toLocaleDateString()}
                      </Typography>
                    </Box>
                  }
                  secondary={i.notes || undefined}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  )
}
